import { registerPlugin } from '@capacitor/core'
import { ActivityEntry } from '../services/api.types'

// Workout confirmation data structure matching React component
export interface WorkoutConfirmationData {
  title: string
  type: WorkoutType
  duration: number // in seconds
  distance: number // in meters
  calories: number
  steps: number // step count during workout
  startTime: string // ISO string
  endTime: string // ISO string
  locations?: any[] // GPS locations array
}

// Define the interface for the ActivityTracker plugin
export interface ActivityTrackerPlugin {
  /**
   * Show the activity tracker interface
   */
  showWorkoutTracker(): Promise<{ success: boolean }>

  /**
   * Hide the activity tracker interface
   */
  hide(): Promise<{ success: boolean }>

  /**
   * Check if the activity tracker is currently visible
   */
  isVisible(): Promise<{ visible: boolean }>

  /**
   * Start a workout with a specific activity type
   * @param options - Configuration with activityType and whether GPS location may be tracked
   *   (false when the user's company has the location-tracking feature disabled — native then skips
   *   all CoreLocation usage and emits no route data)
   */
  startWorkout(options: {
    activityType: WorkoutType
    trackLocation: boolean
  }): Promise<{ success: boolean; activityType: string }>

  /**
   * Confirm and save a completed workout
   * @param options - Updated workout data from confirmation view
   */
  confirmWorkout(options: { title: string; type: WorkoutType }): Promise<{ success: boolean }>

  /**
   * Discard a completed workout
   */
  discardWorkout(): Promise<{ success: boolean }>

  /**
   * Send translated activity labels to the native plugin
   * @param options - Object containing activity labels mapping
   */
  setActivityLabels(options: { labels: Record<string, string> }): Promise<{ success: boolean }>

  /**
   * Listen for workout completion events
   * This will be called when a workout is completed and returns activity data
   */
  addListener(
    eventName: 'workoutCompleted',
    listenerFunc: (data: { activityData: ActivityEntry; routeData?: any[] }) => void,
  ): Promise<{ remove: () => void }>

  /**
   * Listen for workout confirmation required events
   * This will be called when a workout needs confirmation before saving
   */
  addListener(
    eventName: 'workoutConfirmationRequired',
    listenerFunc: (data: { confirmationData: WorkoutConfirmationData }) => void,
  ): Promise<{ remove: () => void }>

  /**
   * Remove all listeners for a specific event
   */
  removeAllListeners(eventName?: string): Promise<void>
}

export type WorkoutType =
  | 'archery'
  | 'badminton'
  | 'barre'
  | 'baseball'
  | 'basketball'
  | 'biking'
  | 'biking.stationary'
  // Not supported in Apple Health, only in Health Connect.
  // | 'bootcamp'
  | 'bowling'
  | 'boxing'
  // Not supported in Apple Health, only in Health Connect.
  // | 'calisthenics'
  | 'cricket'
  | 'cooldown'
  | 'core_training'
  | 'crossfit'
  | 'curling'
  | 'dancing'
  | 'dancing.social'
  | 'dancing.cardio'
  | 'disc_sports'
  | 'elliptical'
  // Not supported in Apple Health, only in Health Connect.
  // | 'exercise_class'
  | 'fencing'
  | 'fitness_gaming'
  | 'fishing'
  | 'flexibility'
  | 'football.american'
  | 'football.australian'
  | 'football.soccer'
  // Not supported in Apple Health, only in Health Connect.
  // | 'frisbee_disc'
  | 'golf'
  // Not supported in Apple Health, only in Health Connect.
  // | 'guided_breathing'
  | 'gymnastics'
  | 'handball'
  | 'interval_training.high_intensity'
  | 'hiking'
  | 'hockey'
  | 'hockey.roller'
  | 'horseback_riding'
  | 'hunting'
  | 'ice_skating'
  | 'jump_rope'
  | 'kickboxing'
  | 'lacrosse'
  | 'martial_arts'
  | 'meditation'
  | 'mixed_metabolic_cardio'
  | 'multi_sport'
  | 'other'
  | 'paddle_sports'
  | 'paddleball'
  // Not supported in Apple Health, only in Health Connect.
  // | 'paragliding'
  | 'pilates'
  | 'play'
  | 'pickleball'
  | 'preparation_and_recovery'
  | 'racquetball'
  | 'rock_climbing'
  | 'rowing'
  | 'rowing.machine'
  | 'rugby'
  | 'running'
  | 'running.treadmill'
  | 'sailing'
  | 'scuba_diving'
  | 'skating'
  | 'skiing'
  | 'skiing.cross_country'
  | 'skiing.downhill'
  | 'snowboarding'
  | 'snowshoeing'
  | 'snow_sports'
  | 'softball'
  | 'squash'
  | 'stair_climbing'
  | 'stair_climbing.machine'
  | 'strength_training'
  | 'strength_training.functional'
  | 'stretching'
  | 'surfing'
  | 'swimming'
  | 'swimming.pool'
  | 'swimming.open_water'
  | 'table_tennis'
  | 'tai_chi'
  | 'tennis'
  | 'track_and_field'
  | 'transition'
  | 'underwater_diving'
  | 'volleyball'
  | 'walking'
  | 'water_fitness'
  | 'water_polo'
  | 'water_sports'
  | 'weightlifting'
  | 'wheelchair'
  | 'wheelchair.walkpace'
  | 'wheelchair.runpace'
  | 'wrestling'
  | 'yoga'

// Register the plugin with Capacitor
const ActivityTracker = registerPlugin<ActivityTrackerPlugin>('ActivityTracker')

export default ActivityTracker
